import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, DatePicker, Descriptions, Form, Input, Modal, Table, Tag } from "antd";
import dayjs from "dayjs";
import { PageHeader } from "../../components/PageHeader";
import { ProjectLink } from "../../components/ProjectLink";
import { SiteSelect } from "../../components/SiteSelect";
import { StaffFormFields } from "./StaffFormFields";
import { staffFormToRequest, staffToFormValues } from "./staffForm";
import { useApi } from "../../helpers/useApi";
import { useSitesQuery } from "../../helpers/queries";
import { hasPermission } from "../../helpers/permissions";
import { useUser } from "../../context/UserContext";
import { DT_FORMATS } from "../../helpers/constants";
import type { Staff, StaffAssignment } from "../../api-models";

// Effective-dated site assignments (spec §28.2) drive who prepopulates onto a Daily
// Attendance Sheet — a second, open-dated assignment is not an error, it's how HR
// documents a temporary transfer/exception (see attendance.go's staffAssignedToSite).
export default function StaffDetailPage() {
  const { id } = useParams();
  const { user } = useUser();
  const canWrite = hasPermission(user, "Staff.Write");
  const { request } = useApi();
  const { data: sites = [] } = useSitesQuery();
  const [staff, setStaff] = useState<Staff | null>(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<StaffAssignment | null>(null);
  const [form] = Form.useForm();
  const [staffOpen, setStaffOpen] = useState(false);
  const [staffForm] = Form.useForm();

  async function load() {
    const res = await request<Staff>({ url: `/staff/${id}` });
    setStaff(res);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ fromDate: dayjs() });
    setOpen(true);
  }

  // Editing rewrites the posting in place — a correction is not a new posting, and adding
  // one would leave the wrong row still effective (see staff.go's handleUpdateStaffAssignment).
  function openEdit(a: StaffAssignment) {
    setEditing(a);
    form.resetFields();
    form.setFieldsValue({
      siteId: a.siteId,
      fromDate: dayjs(a.fromDate),
      toDate: a.toDate ? dayjs(a.toDate) : undefined,
      notes: a.notes,
    });
    setOpen(true);
  }

  async function handleSubmit() {
    const values = await form.validateFields();
    await request({
      method: editing ? "PUT" : "POST",
      url: editing ? `/staff/${id}/assignments/${editing.id}` : `/staff/${id}/assignments`,
      data: {
        siteId: values.siteId,
        fromDate: values.fromDate.format("YYYY-MM-DD"),
        toDate: values.toDate ? values.toDate.format("YYYY-MM-DD") : undefined,
        notes: values.notes ?? "",
      },
    });
    setOpen(false);
    load();
  }

  function openEditStaff() {
    if (!staff) return;
    staffForm.resetFields();
    staffForm.setFieldsValue(staffToFormValues(staff));
    setStaffOpen(true);
  }

  async function handleStaffSubmit() {
    const values = await staffForm.validateFields();
    await request({ method: "PUT", url: `/staff/${id}`, data: staffFormToRequest(values) });
    setStaffOpen(false);
    load();
  }

  if (!staff) return null;

  return (
    <div>
      <PageHeader
        title={staff.name}
        subtitle={[staff.staffCode, staff.designation].filter(Boolean).join(" - ")}
        extra={
          canWrite && (
            <Button size="small" onClick={openEditStaff}>
              Edit
            </Button>
          )
        }
      />
      <Card size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Status">
            <Tag color={staff.employmentStatus === "ACTIVE" ? "green" : "default"}>{staff.employmentStatus}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Designation">{staff.designation}</Descriptions.Item>
          <Descriptions.Item label="Nationality">{staff.nationality}</Descriptions.Item>
          <Descriptions.Item label="Join date">
            {staff.joinDate ? dayjs(staff.joinDate).format(DT_FORMATS.date) : ""}
          </Descriptions.Item>
          <Descriptions.Item label="ID number">{staff.idNumber}</Descriptions.Item>
          <Descriptions.Item label="Passport number">{staff.passportNumber}</Descriptions.Item>
          <Descriptions.Item label="Work permit number">{staff.workPermitNumber}</Descriptions.Item>
          <Descriptions.Item label="Notes">{staff.notes}</Descriptions.Item>
        </Descriptions>
      </Card>
      <Modal title="Edit staff" open={staffOpen} onCancel={() => setStaffOpen(false)} onOk={handleStaffSubmit} destroyOnHidden>
        <Form form={staffForm} layout="vertical">
          <StaffFormFields editing />
        </Form>
      </Modal>
      <Card
        size="small"
        title="Site assignments"
        extra={
          canWrite && (
            <Button type="primary" size="small" onClick={openCreate}>
              Add assignment
            </Button>
          )
        }
      >
        <Table
          rowKey="id"
          size="small"
          pagination={false}
          dataSource={staff.assignments ?? []}
          columns={[
            { title: "Site", render: (_, r: StaffAssignment) => r.site?.name },
            { title: "Project", render: (_, r: StaffAssignment) => <ProjectLink project={r.project} /> },
            { title: "From", dataIndex: "fromDate", render: (v) => dayjs(v).format(DT_FORMATS.date) },
            { title: "To", dataIndex: "toDate", render: (v) => (v ? dayjs(v).format(DT_FORMATS.date) : "Open-ended") },
            { title: "Notes", dataIndex: "notes" },
            ...(canWrite
              ? [
                  {
                    title: "",
                    key: "edit",
                    render: (_: unknown, r: StaffAssignment) => (
                      <Button type="link" onClick={() => openEdit(r)}>
                        Edit
                      </Button>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <Modal
        title={editing ? "Edit site assignment" : "Add site assignment"}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={handleSubmit}
        destroyOnHidden
      >
        <Form form={form} layout="vertical">
          <Form.Item name="siteId" label="Site" rules={[{ required: true }]}>
            <SiteSelect showSearch optionFilterProp="label" options={sites.map((s) => ({ value: s.id, label: s.name }))} />
          </Form.Item>
          <Form.Item name="fromDate" label="From date" rules={[{ required: true }]}>
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="toDate" label="To date (leave blank for open-ended)">
            <DatePicker style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
